/**
 * 대한민국 법정 공휴일 (2024–2030)
 * 고정 공휴일 + 음력 기반 공휴일 + 대체공휴일 포함
 */

/** 고정 공휴일 (MM-DD → 명칭) */
const FIXED_HOLIDAYS: Record<string, string> = {
  '01-01': '신정',
  '03-01': '삼일절',
  '05-05': '어린이날',
  '06-06': '현충일',
  '08-15': '광복절',
  '10-03': '개천절',
  '10-09': '한글날',
  '12-25': '크리스마스',
}

/**
 * 음력 기반 공휴일 (연도별)
 * 설날(음력 1/1 ±1일), 부처님 오신 날(음력 4/8), 추석(음력 8/15 ±1일)
 */
const LUNAR_HOLIDAYS: Record<number, Record<string, string>> = {
  2024: {
    '02-09': '설날 연휴', '02-10': '설날', '02-11': '설날 연휴',
    '05-15': '부처님 오신 날',
    '09-16': '추석 연휴', '09-17': '추석', '09-18': '추석 연휴',
  },
  2025: {
    '01-28': '설날 연휴', '01-29': '설날', '01-30': '설날 연휴',
    '05-05': '부처님 오신 날',
    '10-05': '추석 연휴', '10-06': '추석', '10-07': '추석 연휴',
  },
  2026: {
    '02-16': '설날 연휴', '02-17': '설날', '02-18': '설날 연휴',
    '05-24': '부처님 오신 날',
    '09-24': '추석 연휴', '09-25': '추석', '09-26': '추석 연휴',
  },
  2027: {
    '02-06': '설날 연휴', '02-07': '설날', '02-08': '설날 연휴',
    '05-13': '부처님 오신 날',
    '09-14': '추석 연휴', '09-15': '추석', '09-16': '추석 연휴',
  },
  2028: {
    '01-25': '설날 연휴', '01-26': '설날', '01-27': '설날 연휴',
    '05-02': '부처님 오신 날',
    '10-02': '추석 연휴', '10-03': '추석', '10-04': '추석 연휴',
  },
  2029: {
    '02-12': '설날 연휴', '02-13': '설날', '02-14': '설날 연휴',
    '05-20': '부처님 오신 날',
    '09-21': '추석 연휴', '09-22': '추석', '09-23': '추석 연휴',
  },
  2030: {
    '02-02': '설날 연휴', '02-03': '설날', '02-04': '설날 연휴',
    '05-09': '부처님 오신 날',
    '09-11': '추석 연휴', '09-12': '추석', '09-13': '추석 연휴',
  },
}

/**
 * 대체공휴일 적용 대상 공휴일 (명칭 기준)
 * 설날·추석 연휴, 어린이날: 토요일·일요일 모두 대체 적용
 */
const SUBSTITUTE_SAT_AND_SUN: ReadonlySet<string> = new Set([
  '설날',
  '설날 연휴',
  '추석',
  '추석 연휴',
  '어린이날',
])

/**
 * 대체공휴일 적용 대상 공휴일 (명칭 기준)
 * 일요일에 한해 대체 적용 (토요일은 제외)
 * 신정·현충일은 어느 목록에도 없으므로 대체공휴일 대상이 아님
 */
const SUBSTITUTE_SUNDAY_ONLY: ReadonlySet<string> = new Set([
  '삼일절',
  '광복절',
  '개천절',
  '한글날',
  '부처님 오신 날',
  '크리스마스',
])

/**
 * 겹침(중복) 대체공휴일 적용 대상 공휴일 (명칭 기준)
 * 두 개 이상의 공휴일이 같은 날짜에 겹칠 때, 그 중 하나가 이 목록에 속하면
 * 평일이라도 다음 평일에 대체공휴일을 부여한다 (ROK 「공휴일에 관한 법률」 기준).
 * 부처님 오신 날은 2023년부터 겹침 대체 대상에 포함됨.
 */
const SUBSTITUTE_ON_OVERLAP: ReadonlySet<string> = new Set([
  '설날',
  '설날 연휴',
  '추석',
  '추석 연휴',
  '어린이날',
  '부처님 오신 날',
])

function addDaysStr(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + days)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

/**
 * 해당 연도의 모든 법정 공휴일을 반환
 * 대체공휴일 자동 계산 포함
 */
export function getHolidaysForYear(year: number): Map<string, string> {
  // 날짜별로 겹치는 공휴일 명칭을 모두 누적한다 (덮어쓰기 금지).
  // 같은 날짜에 고정·음력 공휴일이 겹치면 명칭을 병합하고(예: '어린이날·부처님 오신 날'),
  // 겹침 대체공휴일 판정에 두 명칭을 모두 사용한다.
  const names = new Map<string, string[]>()
  const addName = (dateStr: string, name: string) => {
    const list = names.get(dateStr)
    if (list) {
      if (!list.includes(name)) list.push(name)
    } else {
      names.set(dateStr, [name])
    }
  }

  // 1) 고정 공휴일
  for (const [mmdd, name] of Object.entries(FIXED_HOLIDAYS)) {
    addName(`${year}-${mmdd}`, name)
  }

  // 2) 음력 공휴일 (겹치면 병합, 덮어쓰지 않음)
  const lunar = LUNAR_HOLIDAYS[year]
  if (lunar) {
    for (const [mmdd, name] of Object.entries(lunar)) {
      addName(`${year}-${mmdd}`, name)
    }
  }

  const holidays = new Map<string, string>()
  for (const [dateStr, list] of names) {
    holidays.set(dateStr, list.join('·'))
  }

  // 3) 대체공휴일: 「공휴일에 관한 법률」 기준으로 적용 대상 공휴일만 이동
  //    - 설날·추석 연휴, 어린이날: 토요일·일요일 모두 대체 적용
  //    - 삼일절·광복절·개천절·한글날·부처님 오신 날·크리스마스: 일요일에만 대체 적용
  //    - 신정·현충일: 대체공휴일 대상이 아님 (법적으로 제외)
  //    - 겹침(중복): 두 개 이상의 공휴일이 같은 날에 겹치고 그 중 하나가 겹침 대체
  //      대상이면, 평일이라도 다음 평일에 대체공휴일을 부여 (요일 무관)
  //    대체일은 휴일(공휴일·이미 지정된 대체일)이 아닌 가장 가까운 평일로 이동.
  const base = [...names.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  const assignedSubstitutes = new Set<string>()
  for (const [dateStr, list] of base) {
    const dow = new Date(dateStr + 'T00:00:00').getDay()
    const isSat = dow === 6
    const isSun = dow === 0
    let triggers = false
    for (const name of list) {
      if (SUBSTITUTE_SAT_AND_SUN.has(name)) {
        if (isSat || isSun) triggers = true
      } else if (SUBSTITUTE_SUNDAY_ONLY.has(name)) {
        if (isSun) triggers = true
      }
    }
    // 겹침 대체: 같은 날 2개 이상의 공휴일 중 하나라도 겹침 대상이면 요일 무관 적용
    if (!triggers && list.length >= 2 && list.some((n) => SUBSTITUTE_ON_OVERLAP.has(n))) {
      triggers = true
    }
    if (!triggers) continue

    const displayName = list.join('·')

    // 다음 평일(주말·기존 공휴일·이미 지정한 대체일 제외)로 이동
    let sub = addDaysStr(dateStr, 1)
    let subDow = new Date(sub + 'T00:00:00').getDay()
    while (holidays.has(sub) || assignedSubstitutes.has(sub) || subDow === 0 || subDow === 6) {
      sub = addDaysStr(sub, 1)
      subDow = new Date(sub + 'T00:00:00').getDay()
    }
    assignedSubstitutes.add(sub)
    holidays.set(sub, `대체공휴일 (${displayName})`)
  }

  return holidays
}

// 연도별 캐시
const cache = new Map<number, Map<string, string>>()

/** dateStr(YYYY-MM-DD)이 공휴일이면 명칭 반환, 아니면 undefined */
export function getHolidayName(dateStr: string): string | undefined {
  const year = parseInt(dateStr.slice(0, 4), 10)
  if (!cache.has(year)) {
    cache.set(year, getHolidaysForYear(year))
  }
  return cache.get(year)!.get(dateStr)
}

/** dateStr이 공휴일인지 여부 */
export function isHoliday(dateStr: string): boolean {
  return getHolidayName(dateStr) !== undefined
}
